<template>
  <div
    class="preset-mockup-color-picker"
    :class="{ 'preset-mockup-color-picker--mockup-modal': isMockupModal }"
  >
    <div class="preset-mockup-color-picker__picker">
      <span v-if="isMockupModal" class="preset-mockup-color-picker__label">
        Preview background
      </span>
      <ColorPicker
        :value="presetMockupColor"
        :size="colorPickerSize"
        :colors="colors"
        modal-position="bottom-right"
        @change="handleColorChange"
      />
      <MockupButton v-if="!isMockupModal" :index="index" />
    </div>
    <p class="preset-mockup-color-picker__info">
      T-shirt not included. Need one?
      <a href="/?category=T-Shirt&style=Short+Sleeve">Browse our selection!</a>
    </p>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { isPhone, isTablet } from "common/utils/browser";
import ColorPicker from "components/jiffy/vue-product-details-dtf/ColorPicker.vue";
import { trackRudderstackImagePlacementColor } from "common/utils/rudderstack/tracker";
import { useDtfStore } from "../store";
import { PRESET_MOCKUP_COLORS } from "../common/constants";
import MockupButton from "./MockupButton.vue";

const props = defineProps({
  index: {
    type: Number,
    default: 0
  },
  isMockupModal: {
    type: Boolean,
    default: false
  }
});

const store = useDtfStore();

const isPhoneDevice = isPhone();
const isTabletDevice = isTablet();

const presetMockupColor = computed(() => store.presetMockupColor[props.index] || "#E1E1E1");

const colorPickerSize = computed(() => {
  if (isPhoneDevice) return "large";
  if (isTabletDevice) return "medium";
  return "small";
});

const colors = computed(() => {
  if (!props.isMockupModal) {
    if (isPhoneDevice) return PRESET_MOCKUP_COLORS.slice(0, 5);
    if (isTabletDevice) return PRESET_MOCKUP_COLORS.slice(0, 6);
    return PRESET_MOCKUP_COLORS.filter(color => !color.isModalColor);
  }
  return PRESET_MOCKUP_COLORS;
});

const handleColorChange = color => {
  store.setPresetMockupColor({ index: props.index, color });

  const colorName = PRESET_MOCKUP_COLORS.find(c => c.value === color)?.name || "custom";
  const trackProperties = {
    color: colorName,
    hexcode: color
  };
  trackRudderstackImagePlacementColor(trackProperties);
};
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.preset-mockup-color-picker {
  $this: &;

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;

  &--mockup-modal {
    border-radius: 8px;
    background-color: $crafterpay-gray-modern-100;
    width: 100%;

    #{$this}__picker {
      background-color: transparent;
      padding-bottom: 0;
      flex-direction: column;
      align-items: flex-start;
    }

    #{$this}__info {
      margin: 10px;
    }
  }

  &__picker {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 8px;
    border-radius: 8px;
    background-color: $crafterpay-gray-modern-100;
    width: 100%;

    @include with-desktop {
      flex-direction: row;
      align-items: center;
    }
  }

  &__label {
    font-family: $font-family-secondary;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
    text-align: left;
    color: $crafterpay-gray-modern-800;
    flex-grow: 1;
  }

  &__info {
    font-family: $font-family-secondary;
    font-size: 13px;
    font-style: italic;
    font-weight: 400;
    line-height: 14px;
    color: $crafterpay-gray-modern-600;
    margin: 12px 0 0 0;

    a {
      text-decoration: underline;
    }
  }

  .vue-tooltip {
    margin-left: auto;
  }
}
</style>
